import React from 'react';

import { Avatar, Link, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import type { TopStoryDesktop, TopStoryMobile } from '#src/app/pages/homepage/shared/types';

import { TopImpactStoryBadge } from './components/TopImpactStoryBadge';

type Props = {
	trackStoryClick: (url: string) => void;
	story: TopStoryDesktop & TopStoryMobile;
	isMobile?: boolean;
	isCenteredOnMobile?: boolean;
	onStoryFocus?: () => void;
	/**
	 * "staggered" (default) pulls non-centered bubbles out of flow via `story.top`/`story.transform`,
	 * so the row's height is driven by the centered bubble alone - matches the desktop orbit layout.
	 * "row" keeps every bubble in normal flow so the row sizes itself to the tallest bubble instead.
	 */
	layout?: 'staggered' | 'row';
};

export const TopImpactStoryBubble: React.FC<Props> = ({
	story,
	isMobile = false,
	isCenteredOnMobile = false,
	layout = 'staggered',
	onStoryFocus,
	trackStoryClick,
}) => {
	return (
		<div className={cn('flex shrink-0 flex-col items-center', layout === 'row' ? 'justify-start' : 'justify-center')}>
			<div
				className={cn(
					'group relative',
					!isMobile && layout === 'staggered' && !story.isDesktopCentered && ['absolute', story.top, story.transform],
				)}
				data-testid="top-impact-story-bubble-wrapper"
			>
				<Link
					href={story.url}
					className="relative z-10 block w-[210px] text-center outline-none lg:w-[180px] xl:w-[210px]"
					onClick={() => trackStoryClick(story.url)}
					aria-label={story.ariaLabel}
					data-testid={`${story.dataTestIdBase}-link`}
					data-qa="top-impact-story-link"
					onFocus={onStoryFocus}
					tabIndex={story.tabIndex}
					aria-hidden={story.ariaHidden}
					underline={false}
				>
					<Avatar
						src={story.image}
						alt={story.altText}
						size={210}
						className={cn(
							[
								'object-cover',
								'relative z-10',
								'before:absolute before:inset-x-[-2px] before:inset-y-[4px] before:-top-2 before:bottom-2',
								'before:rounded-full before:content-[""]',
								'before:-z-1 before:bg-[#4AAEFF] before:blur-lg',
								'before:duration-t4 before:opacity-0 before:transition-opacity before:will-change-[opacity]',
							],
							{
								'before:opacity-100': isCenteredOnMobile && isMobile,
								'group-focus-within:before:opacity-100 group-hover:before:opacity-100': !isMobile,
							},
						)}
						data-testid={`${story.dataTestIdBase}-image`}
						aria-hidden
					/>

					<TopImpactStoryBadge
						labelTitle={story.labelTitle}
						labelText={story.labelText}
						dataTestId={`${story.dataTestIdBase}-badge`}
						isCenteredOnMobile={isCenteredOnMobile}
						isMobile={isMobile}
					/>

					{!isMobile && (
						<Text
							as="p"
							size="large"
							className="text-typography-strong duration-t2 line-clamp-3 pt-11 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
							data-testid={`${story.dataTestIdBase}-summary`}
						>
							{story.summary}
						</Text>
					)}
				</Link>
			</div>
		</div>
	);
};
